"use client";

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  Ban,
  BookOpen,
  Briefcase,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  CircleDashed,
  Clock,
  ExternalLink,
  FileText,
  Gavel,
  History,
  Inbox,
  Info,
  Link2,
  Search,
  ShieldQuestion,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge, timeAgo } from "@/components/mission/sections";
import type {
  ActionEntry,
  DecisionEntry,
  EarningsEntry,
  MissionData,
  NextAction,
  Opportunity,
  ResearchDoc,
} from "@/types/mission";

/* -------------------------------- pipeline --------------------------------- */

type Filter = "all" | "viable" | "rejected";

/**
 * Listens for "mission:focus-item" events (dispatched by the timeline and
 * command palette when a user jumps to a specific row): scrolls the target
 * into view and flashes it. Purely client-side, idempotent.
 */
function useMissionFocus(prefix: string, onFocus?: (id: string) => void) {
  const cbRef = useRef(onFocus);
  /* keep the callback ref fresh after every render (latest-ref pattern —
     never assigned during render itself) */
  useEffect(() => {
    cbRef.current = onFocus;
  });
  useEffect(() => {
    const handler = (e: Event) => {
      const id = (e as CustomEvent<{ id?: string }>).detail?.id;
      if (!id) return;
      cbRef.current?.(id);
      const el = document.getElementById(`${prefix}-${id}`);
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.classList.add("mission-flash");
      window.setTimeout(() => el.classList.remove("mission-flash"), 2_000);
    };
    window.addEventListener("mission:focus-item", handler);
    return () => window.removeEventListener("mission:focus-item", handler);
  }, [prefix]);
}

function isViable(o: Opportunity) {
  return o.status === "validated" || o.status === "validated-with-blockers" || o.status === "active";
}
function isRejected(o: Opportunity) {
  return o.status === "rejected" || o.status === "dead";
}

/* truthful status distribution — segments map one-to-one to pipeline.json statuses */
function StatusDistribution({
  pipeline,
  onFilter,
  activeFilter,
}: {
  pipeline: Opportunity[];
  onFilter: (f: Filter) => void;
  activeFilter: Filter;
}) {
  const buckets = useMemo(() => {
    const b = { viable: 0, blocked: 0, researching: 0, rejected: 0 };
    for (const o of pipeline) {
      if (isViable(o)) b.viable++;
      else if (o.status === "blocked") b.blocked++;
      else if (o.status === "researching") b.researching++;
      else b.rejected++;
    }
    return b;
  }, [pipeline]);
  const total = pipeline.length || 1;

  const segments = [
    { key: "viable", label: `viable ${buckets.viable}`, width: (buckets.viable / total) * 100, className: "bg-emerald-500/70 hover:bg-emerald-400", filter: "viable" as Filter },
    { key: "blocked", label: `blocked ${buckets.blocked}`, width: (buckets.blocked / total) * 100, className: "bg-amber-500/60 hover:bg-amber-400", filter: "all" as Filter },
    { key: "researching", label: `researching ${buckets.researching}`, width: (buckets.researching / total) * 100, className: "bg-zinc-500/70 hover:bg-zinc-400", filter: "all" as Filter },
    { key: "rejected", label: `rejected ${buckets.rejected}`, width: (buckets.rejected / total) * 100, className: "bg-rose-500/60 hover:bg-rose-400", filter: "rejected" as Filter },
  ].filter((s) => s.width > 0);

  return (
    <div className="space-y-1.5">
      <div
        className="flex h-1.5 w-full gap-px overflow-hidden rounded-full"
        role="img"
        aria-label={`Pipeline distribution: ${buckets.viable} viable, ${buckets.blocked} blocked, ${buckets.researching} researching, ${buckets.rejected} rejected`}
      >
        {segments.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={() => onFilter(s.filter)}
            title={`${s.label} — click to ${s.filter === "all" ? "show all" : `filter ${s.filter}`}`}
            aria-label={`${s.label} segment`}
            className={`h-full min-w-[3%] cursor-pointer transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 ${s.className} ${
              (s.filter !== "all" && activeFilter === s.filter) ? "opacity-100" : "opacity-80"
            }`}
            style={{ width: `${Math.max(s.width, 2)}%` }}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-zinc-600">
        {segments.map((s) => (
          <span key={s.key} className="inline-flex items-center gap-1">
            <span className={`size-1.5 rounded-[2px] ${s.className.split(" ")[0]}`} aria-hidden="true" />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function EvidenceList({ items, empty }: { items: string[]; empty: string }) {
  if (items.length === 0) return <p className="text-[11px] text-zinc-600">{empty}</p>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-1.5 text-[11px] leading-relaxed text-zinc-400">
          <span className="mt-1 size-1 shrink-0 rounded-full bg-zinc-600" aria-hidden="true" />
          <span className="min-w-0">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function SourceLinks({ sources }: { sources: string[] }) {
  const host = (u: string) => {
    try {
      return new URL(u).hostname.replace("www.", "");
    } catch {
      return u;
    }
  };
  if (sources.length === 0) return <p className="text-[11px] text-zinc-600">no source links recorded</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {sources.map((s) => (
        <a
          key={s}
          href={s}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 rounded-md border border-zinc-700/60 bg-zinc-800/40 px-2 py-0.5 font-mono text-[10px] text-emerald-400/90 transition-colors hover:border-emerald-500/40 hover:text-emerald-300"
        >
          {host(s)}
          <ExternalLink className="size-2.5" aria-hidden="true" />
        </a>
      ))}
    </div>
  );
}

function PipelineExpand({ o }: { o: Opportunity }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="grid gap-4 rounded-lg border border-zinc-800/60 bg-zinc-950/40 p-4 sm:grid-cols-2"
    >
      <div>
        <p className="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-400/80">
          <ShieldQuestion className="size-3" aria-hidden="true" />
          Blockers ({o.blockers.length})
        </p>
        <EvidenceList items={o.blockers} empty="none recorded" />
        <p className="mb-1.5 mt-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Requirements ({o.requirements.length})
        </p>
        <EvidenceList items={o.requirements} empty="none recorded" />
      </div>
      <div>
        <p className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Evidence ({o.evidence.length})
        </p>
        <EvidenceList items={o.evidence} empty="no evidence recorded" />
        <p className="mb-1.5 mt-3 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          Sources
        </p>
        <SourceLinks sources={o.sources} />
        <p className="mt-3 font-mono text-[10px] text-zinc-600">
          effort: {o.effort} · updated {o.last_updated.slice(0, 10)}
        </p>
      </div>
    </motion.div>
  );
}

export function PipelineView({ pipeline }: { pipeline: Opportunity[] }) {
  const [filter, setFilter] = useState<Filter>("all");
  const [expanded, setExpanded] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const rank = (o: Opportunity) => (isViable(o) ? 0 : o.status === "researching" ? 1 : 2);
    return [...pipeline].sort((a, b) => {
      const r = rank(a) - rank(b);
      if (r !== 0) return r;
      const pa = a.priority ?? 99;
      const pb = b.priority ?? 99;
      return pa - pb;
    });
  }, [pipeline]);

  const visible = sorted.filter((o) =>
    filter === "all" ? true : filter === "viable" ? isViable(o) : isRejected(o),
  );

  const viableCount = pipeline.filter(isViable).length;
  const rejectedCount = pipeline.filter(isRejected).length;

  return (
    <div className="space-y-4">
      <StatusDistribution pipeline={pipeline} onFilter={setFilter} activeFilter={filter} />
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Pipeline filter">
        {(
          [
            ["all", `All · ${pipeline.length}`],
            ["viable", `Viable · ${viableCount}`],
            ["rejected", `Rejected · ${rejectedCount}`],
          ] as Array<[Filter, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            aria-pressed={filter === key}
            className={`rounded-md border px-2.5 py-1 font-mono text-[11px] font-semibold tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 ${
              filter === key
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                : "border-zinc-700/60 bg-zinc-800/30 text-zinc-500 hover:border-zinc-600 hover:text-zinc-300"
            }`}
          >
            {label}
          </button>
        ))}
        <span className="ml-auto hidden font-mono text-[10px] text-zinc-600 sm:block">
          sorted by execution priority
        </span>
      </div>

      {/* md+ table */}
      <div className="hidden overflow-hidden rounded-lg border border-zinc-800/70 md:block">
        <ScrollArea className="h-[26rem]">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-zinc-900/95 backdrop-blur">
              <TableRow className="border-zinc-800/70 hover:bg-transparent">
                <TableHead className="h-9 w-8 font-mono text-[10px] uppercase tracking-wider text-zinc-500" />
                <TableHead className="h-9 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Opportunity
                </TableHead>
                <TableHead className="h-9 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Category
                </TableHead>
                <TableHead className="h-9 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Status
                </TableHead>
                <TableHead className="h-9 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  EV
                </TableHead>
                <TableHead className="h-9 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                  Next
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((o, idx) => (
                <Fragment key={o.id}>
                  <TableRow
                    className={`cursor-pointer border-zinc-800/50 align-top transition-colors outline-none hover:bg-zinc-900/60 focus-visible:bg-zinc-900/60 focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-emerald-500/50 ${
                      idx % 2 === 1 ? "bg-zinc-900/25" : ""
                    }`}
                    onClick={() => setExpanded((e) => (e === o.id ? null : o.id))}
                    tabIndex={0}
                    role="button"
                    aria-expanded={expanded === o.id}
                    aria-label={`${o.id} ${o.name} — ${expanded === o.id ? "collapse" : "expand"} details`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setExpanded((x) => (x === o.id ? null : o.id));
                      }
                    }}
                  >
                    <TableCell className="py-3 pl-3">
                      {expanded === o.id ? (
                        <ChevronDown className="size-3.5 text-emerald-400" aria-hidden="true" />
                      ) : (
                        <ChevronRight className="size-3.5 text-zinc-600" aria-hidden="true" />
                      )}
                      <span className="sr-only">{expanded === o.id ? "collapse" : "expand"} details</span>
                    </TableCell>
                    <TableCell className="max-w-[26rem] break-words py-3 pr-4">
                      <p className="break-words text-sm font-medium text-zinc-200">
                        <span className="mr-1.5 font-mono text-[10px] text-zinc-600">{o.id}</span>
                        {o.name}
                      </p>
                      <p className="mt-1 line-clamp-2 break-words text-xs leading-relaxed text-zinc-500" title={o.thesis}>
                        {o.thesis}
                      </p>
                      {o.blockers.length > 0 && isViable(o) && (
                        <p className="mt-1 font-mono text-[10px] text-amber-500/80">
                          {o.blockers.length} blocker{o.blockers.length > 1 ? "s" : ""}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="py-3 pr-4 font-mono text-[11px] text-zinc-400">
                      {o.category}
                    </TableCell>
                    <TableCell className="py-3 pr-4">
                      <StatusBadge status={o.status} />
                    </TableCell>
                    <TableCell className="py-3 pr-4 font-mono text-[11px] text-zinc-400" title={o.effort}>
                      {o.ev}
                    </TableCell>
                    <TableCell className="max-w-[16rem] py-3 pr-4">
                      <p className="line-clamp-3 break-words text-xs leading-relaxed text-zinc-400" title={o.next_action}>
                        {o.next_action}
                      </p>
                    </TableCell>
                  </TableRow>
                  {expanded === o.id && (
                    <TableRow className="border-zinc-800/50 hover:bg-transparent">
                      <TableCell colSpan={6} className="py-0">
                        <div className="py-2">
                          <PipelineExpand o={o} />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>

      {/* mobile cards */}
      <div className="space-y-2.5 md:hidden">
        {visible.map((o) => (
          <Card key={o.id} className="border-zinc-800/70 bg-zinc-900/40 shadow-none">
            <CardContent className="space-y-2 p-3.5">
              <button
                type="button"
                className="flex w-full items-start justify-between gap-2 text-left"
                onClick={() => setExpanded((e) => (e === o.id ? null : o.id))}
                aria-expanded={expanded === o.id}
              >
                <p className="min-w-0 flex-1 break-words text-sm font-medium leading-snug text-zinc-200">
                  <span className="mr-1.5 font-mono text-[10px] text-zinc-600">{o.id}</span>
                  {o.name}
                </p>
                <StatusBadge status={o.status} />
              </button>
              <p className="line-clamp-3 break-words text-xs leading-relaxed text-zinc-500">{o.thesis}</p>
              <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[10px] text-zinc-600">
                <span>{o.category}</span>
                <span>EV: {o.ev}</span>
                <span>effort: {o.effort}</span>
              </div>
              {expanded === o.id && <PipelineExpand o={o} />}
              <p className="break-words border-t border-zinc-800/60 pt-2 text-xs leading-relaxed text-zinc-400">
                <span className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">next: </span>
                {o.next_action}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------- earnings --------------------------------- */

export function EarningsView({ earnings }: { earnings: EarningsEntry[] }) {
  useMissionFocus("earn");
  if (earnings.length === 0) {
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-8 text-center">
        <Inbox className="size-8 text-zinc-700" aria-hidden="true" />
        <p className="font-mono text-sm font-semibold text-zinc-400">No earnings yet</p>
        <p className="max-w-md text-xs leading-relaxed text-zinc-600">
          This is the honest state of the mission. When a platform pays out or a transaction lands
          on one of the receiving wallets, it will appear here with its txid and status — nothing
          simulated, ever.
        </p>
        {/* celebration contract — what exactly will open the gate (truthful, checks only) */}
        <div className="w-full max-w-md rounded-md border border-zinc-800/70 bg-zinc-900/30 p-3 text-left">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
            What will count
          </p>
          <ul className="mt-1.5 space-y-1 text-[11px] leading-relaxed text-zinc-600">
            <li className="flex gap-1.5">
              <span className="font-mono text-emerald-500" aria-hidden="true">✓</span>
              settled on-chain USDC / payouts with a viewable txid or platform link
            </li>
            <li className="flex gap-1.5">
              <span className="font-mono text-emerald-500" aria-hidden="true">✓</span>
              non-zero balance on a monitored receiving wallet (checked every wake)
            </li>
            <li className="flex gap-1.5">
              <span className="font-mono text-rose-400/80" aria-hidden="true">✗</span>
              402 payment handshakes — they prove the rail, not an earning
            </li>
          </ul>
          <p className="mt-2 border-t border-zinc-800/60 pt-2 text-[11px] leading-relaxed text-zinc-600">
            The first verified entry unlocks a full-dashboard celebration state — only real data can
            open that gate.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-lg border border-zinc-800/70">
      <Table>
        <TableHeader>
          <TableRow className="border-zinc-800/70 hover:bg-transparent">
            {["When", "Source", "Asset", "Amount", "Status", "Evidence"].map((h) => (
              <TableHead key={h} className="h-9 font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                {h}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {earnings.map((e) => (
            <TableRow key={e.id} id={`earn-${e.id}`} className="border-zinc-800/50 transition-colors hover:bg-zinc-900/40">
              <TableCell className="py-3 font-mono text-xs text-zinc-400">{timeAgo(e.ts)}</TableCell>
              <TableCell className="py-3 text-xs text-zinc-300">{e.source_opportunity}</TableCell>
              <TableCell className="py-3 font-mono text-xs text-zinc-400">
                {e.chain} · {e.asset}
              </TableCell>
              <TableCell className="py-3 font-mono text-xs text-zinc-100">{e.amount}</TableCell>
              <TableCell className="py-3">
                <StatusBadge status={e.status} />
              </TableCell>
              <TableCell className="py-3 text-xs">
                {e.txid_or_link ? (
                  <a
                    href={e.txid_or_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-emerald-400 hover:underline"
                  >
                    view <ExternalLink className="size-3" />
                  </a>
                ) : (
                  <span className="text-zinc-600">pending</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/* -------------------------------- timeline -------------------------------- */

/**
 * One chronological mission story: actions, decisions, research notes and
 * earnings interleaved by timestamp. Every item renders only what exists in
 * mission/*.json — no synthesis, no fabrication.
 */
export type TimelineItem =
  | { kind: "action"; ts: string; id: string; text: string; meta: string; ok: boolean }
  | { kind: "decision"; ts: string; id: string; text: string; meta: string }
  | { kind: "research"; ts: string; id: string; text: string; meta: string }
  | { kind: "earning"; ts: string; id: string; text: string; meta: string; status: string };

const TIMELINE_META: Record<
  TimelineItem["kind"],
  { label: string; dot: string; icon: React.ReactNode; chip: string }
> = {
  action: {
    label: "ACTION",
    dot: "border-teal-500/60 bg-teal-500/30",
    icon: <CircleCheck className="size-3" aria-hidden="true" />,
    chip: "border-teal-500/30 bg-teal-500/10 text-teal-300",
  },
  decision: {
    label: "DECISION",
    dot: "border-violet-500/60 bg-violet-500/30",
    icon: <Gavel className="size-3" aria-hidden="true" />,
    chip: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  },
  research: {
    label: "RESEARCH",
    dot: "border-sky-500/60 bg-sky-500/30",
    icon: <FileText className="size-3" aria-hidden="true" />,
    chip: "border-sky-500/30 bg-sky-500/10 text-sky-300",
  },
  earning: {
    label: "EARNING",
    dot: "border-amber-500/60 bg-amber-500/30",
    icon: <Briefcase className="size-3" aria-hidden="true" />,
    chip: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  },
};

const OPEN_TARGET: Record<TimelineItem["kind"], string> = {
  action: "Evidence Log",
  decision: "Decisions",
  research: "Research",
  earning: "Earnings",
};

export function TimelineView({
  data,
  onOpen,
}: {
  data: MissionData;
  onOpen: (item: TimelineItem) => void;
}) {
  /* kind filter — quick scoping across the merged stream */
  const [kind, setKind] = useState<TimelineItem["kind"] | "all">("all");

  const items = useMemo<TimelineItem[]>(() => {
    const list: TimelineItem[] = [];
    for (const a of data.actions) {
      const ts = a.ts ?? a.timestamp ?? "";
      if (!ts) continue;
      list.push({
        kind: "action",
        ts,
        id: a.id,
        text: a.summary ?? a.action ?? "(no summary recorded)",
        meta: `session ${a.session} · ${a.agent ?? "cron-agent"}`,
        ok: a.result === "success",
      });
    }
    for (const d of data.decisions) {
      list.push({ kind: "decision", ts: d.ts, id: d.id, text: d.title, meta: d.decision.slice(0, 140) });
    }
    for (const r of data.research) {
      list.push({
        kind: "research",
        ts: `${r.date}T12:00:00Z`,
        id: r.filename,
        text: r.title,
        meta: `${(r.size_bytes / 1024).toFixed(1)} KB evidence note`,
      });
    }
    for (const e of data.earnings) {
      list.push({
        kind: "earning",
        ts: e.ts,
        id: e.id,
        text: `${e.amount} ${e.asset} — ${e.source_opportunity}`,
        meta: e.txid_or_link || "pending evidence",
        status: e.status,
      });
    }
    return list.sort((a, b) => b.ts.localeCompare(a.ts));
  }, [data]);

  const kindCounts = useMemo(() => {
    const c: Record<string, number> = { action: 0, decision: 0, research: 0, earning: 0 };
    for (const it of items) c[it.kind] += 1;
    return c;
  }, [items]);

  const filtered = useMemo(
    () => (kind === "all" ? items : items.filter((it) => it.kind === kind)),
    [items, kind],
  );

  /* group by calendar day for scannability */
  const groups = useMemo(() => {
    const map = new Map<string, TimelineItem[]>();
    for (const it of filtered) {
      const day = it.ts.slice(0, 10);
      const arr = map.get(day);
      if (arr) arr.push(it);
      else map.set(day, [it]);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const FILTERS: { id: typeof kind; label: string; count: number; chip: string }[] = [
    { id: "all", label: "All", count: items.length, chip: "border-zinc-700 bg-zinc-800/40 text-zinc-300" },
    { id: "action", label: "Actions", count: kindCounts.action, chip: "border-sky-500/30 bg-sky-500/10 text-sky-300" },
    { id: "decision", label: "Decisions", count: kindCounts.decision, chip: "border-violet-500/30 bg-violet-500/10 text-violet-300" },
    { id: "research", label: "Research", count: kindCounts.research, chip: "border-teal-500/30 bg-teal-500/10 text-teal-300" },
    { id: "earning", label: "Earnings", count: kindCounts.earning, chip: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
  ];
  const today = new Date().toISOString().slice(0, 10);

  if (items.length === 0) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-8 font-mono text-xs text-zinc-600">
        no mission history recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      <p className="text-[11px] leading-relaxed text-zinc-600">
        Full mission history — every logged action, decision, research note and (someday) verified
        earning, newest first. {items.length} events across {groups.length} days. Select any event
        to jump straight to its record in the source view.
      </p>
      {/* kind filter chips */}
      <div
        className="flex flex-wrap items-center gap-1.5"
        role="group"
        aria-label="Filter timeline by event kind"
      >
        {FILTERS.map((f) => {
          const active = kind === f.id;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => setKind(f.id)}
              aria-pressed={active}
              className={`rounded-md border px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 motion-reduce:transition-none ${
                active
                  ? f.chip
                  : "border-zinc-800/70 bg-zinc-900/30 text-zinc-600 hover:border-zinc-700 hover:text-zinc-400"
              }`}
            >
              {f.label}
              <span className="ml-1.5 tabular-nums opacity-70">{f.count}</span>
            </button>
          );
        })}
      </div>
      <ScrollArea className="h-[28rem] rounded-lg border border-zinc-800/70 bg-zinc-950/30 mission-scroll">
        <div className="p-4">
          {filtered.length === 0 && (
            <p className="flex min-h-[10rem] items-center justify-center font-mono text-xs text-zinc-600">
              no {FILTERS.find((f) => f.id === kind)?.label.toLowerCase() ?? ""} events recorded yet — honest
              zero, nothing simulated
            </p>
          )}
          {groups.map(([day, dayItems]) => (
            <section key={day} className="mb-5 last:mb-0">
              <p
                className={`sticky top-0 z-10 -mx-1 mb-2 px-1 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] backdrop-blur ${
                  day === today ? "text-emerald-400" : "text-zinc-500"
                } bg-zinc-950/90`}
              >
                {day}
                {day === today && (
                  <span className="ml-1.5 rounded border border-emerald-500/40 bg-emerald-500/10 px-1 py-px text-[9px] font-bold text-emerald-300">
                    today
                  </span>
                )}
                <span className="ml-2 font-normal normal-case tracking-normal text-zinc-600">
                  {dayItems.length} event{dayItems.length === 1 ? "" : "s"}
                </span>
              </p>
              <ol className="relative space-y-0 border-l border-zinc-800/70 pl-5">
                {dayItems.map((it) => {
                  const m = TIMELINE_META[it.kind];
                  const target = OPEN_TARGET[it.kind];
                  return (
                    <li key={`${it.kind}-${it.id}`} className="group relative pb-4 last:pb-0">
                      <span
                        className={`absolute -left-[27px] top-0.5 flex size-[18px] items-center justify-center rounded-full border ${m.dot}`}
                        aria-hidden="true"
                      >
                        <span className="text-zinc-200/90">{m.icon}</span>
                      </span>
                      <button
                        type="button"
                        onClick={() => onOpen(it)}
                        aria-label={`Open ${m.label.toLowerCase()} ${it.id} in ${target}`}
                        title={`Open in ${target}`}
                        className="block w-full min-w-0 cursor-pointer rounded-md border border-zinc-800/50 bg-zinc-900/30 px-3 py-2 text-left transition-all duration-150 hover:-translate-y-px hover:border-zinc-600 hover:bg-zinc-900/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
                      >
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                          <span
                            className={`rounded border px-1.5 py-px font-mono text-[9px] font-semibold uppercase tracking-wider ${m.chip}`}
                          >
                            {m.label}
                          </span>
                          <span className="font-mono text-[10px] text-zinc-600">{it.id}</span>
                          <span className="font-mono text-[10px] tabular-nums text-zinc-600">
                            {it.ts.slice(11, 16)}Z
                          </span>
                          {it.kind === "action" && !it.ok && (
                            <Ban className="size-3 text-rose-500" aria-label="failed" />
                          )}
                          <span
                            className="ml-auto hidden items-center gap-0.5 font-mono text-[9px] text-zinc-600 opacity-0 transition-opacity group-hover:opacity-100 sm:flex"
                            aria-hidden="true"
                          >
                            <Link2 className="size-3" />
                            {target}
                          </span>
                        </span>
                        <span className="mt-1 block text-xs leading-relaxed text-zinc-300 line-clamp-2" title={it.text}>
                          {it.text}
                        </span>
                        <span className="mt-0.5 block truncate font-mono text-[10px] text-zinc-600">{it.meta}</span>
                      </button>
                    </li>
                  );
                })}
              </ol>
            </section>
          ))}
        </div>
      </ScrollArea>
    </div>
  );
}

/* --------------------------------- evidence -------------------------------- */

const TYPE_STYLES: Record<string, string> = {
  research: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  security: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  persistence: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  monitoring: "border-teal-500/30 bg-teal-500/10 text-teal-300",
  bootstrap: "border-zinc-600/40 bg-zinc-700/20 text-zinc-300",
};

/* Evidence type families — the raw actions.json `type` field is a long tail
   ("monitor", "monitoring", "monitor-verify", "deep-research", …). These
   families mirror the attribution logic in wake-cadence.tsx so both views
   speak the same language. Anything unmatched lands honestly in "other". */
function evidenceFamily(t: string): "loop" | "monitor" | "research" | "incident" | "other" {
  if (t === "mission-loop") return "loop";
  if (t.startsWith("monitor")) return "monitor";
  if (t.startsWith("deep-research") || t === "research") return "research";
  if (t === "incident-response") return "incident";
  return "other";
}

const EVIDENCE_FAMILY_STYLES: Record<string, string> = {
  all: "border-zinc-700 bg-zinc-800/40 text-zinc-300",
  loop: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  monitor: "border-teal-500/30 bg-teal-500/10 text-teal-300",
  research: "border-violet-500/30 bg-violet-500/10 text-violet-300",
  incident: "border-rose-500/30 bg-rose-500/10 text-rose-300",
  other: "border-zinc-600/40 bg-zinc-700/20 text-zinc-400",
};

/* Outcome classification — actions.json `result` is free text, so honesty
   demands a conservative tri-state instead of the old binary "success? green
   : red" which mislabeled entries like "success — lint clean…" as failures.
   Only explicit failure markers (blocked…, outage, failed, error) count as
   failed; every other recorded free-text outcome is "recorded" (neutral),
   and a missing field is "none". Sentiment is never guessed. */
type Outcome = "success" | "failed" | "recorded" | "none";

function outcomeOf(a: ActionEntry): Outcome {
  const r = (a.result ?? "").trim();
  if (!r) return "none";
  if (r === "success" || r.startsWith("success")) return "success";
  if (/^blocked|\boutage\b|\bfailed\b|\berror\b/i.test(r)) return "failed";
  return "recorded";
}

const OUTCOME_META: Record<Outcome, { label: string; chip: string }> = {
  success: { label: "Success", chip: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" },
  recorded: { label: "Recorded", chip: "border-amber-500/30 bg-amber-500/10 text-amber-300" },
  failed: { label: "Failed", chip: "border-rose-500/30 bg-rose-500/10 text-rose-300" },
  none: { label: "Not recorded", chip: "border-zinc-600/40 bg-zinc-700/20 text-zinc-400" },
};

export function EvidenceView({ actions }: { actions: ActionEntry[] }) {
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState<string>("all");
  const [outcome, setOutcome] = useState<Outcome | "all">("all");
  useMissionFocus("evi");
  const ordered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rev = [...actions].reverse();
    return rev.filter((a) => {
      if (family !== "all" && evidenceFamily(a.type ?? "") !== family) return false;
      if (outcome !== "all" && outcomeOf(a) !== outcome) return false;
      if (!q) return true;
      return (
        (a.id ?? "").toLowerCase().includes(q) ||
        (a.agent ?? "").toLowerCase().includes(q) ||
        (a.type ?? "").toLowerCase().includes(q) ||
        (a.summary ?? "").toLowerCase().includes(q) ||
        String(a.session).includes(q)
      );
    });
  }, [actions, query, family, outcome]);

  const familyCounts = useMemo(() => {
    const c: Record<string, number> = { all: actions.length, loop: 0, monitor: 0, research: 0, incident: 0, other: 0 };
    for (const a of actions) c[evidenceFamily(a.type ?? "")] += 1;
    return c;
  }, [actions]);

  const outcomeCounts = useMemo(() => {
    const c: Record<Outcome | "all", number> = { all: actions.length, success: 0, recorded: 0, failed: 0, none: 0 };
    for (const a of actions) c[outcomeOf(a)] += 1;
    return c;
  }, [actions]);

  const FAMILIES: { id: string; label: string }[] = [
    { id: "all", label: "All" },
    { id: "loop", label: "Loop" },
    { id: "monitor", label: "Monitor" },
    { id: "research", label: "Research" },
    { id: "incident", label: "Incidents" },
    { id: "other", label: "Other" },
  ];

  const OUTCOMES: (Outcome | "all")[] = ["all", "success", "recorded", "failed", "none"];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:max-w-sm">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-zinc-600"
            aria-hidden="true"
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="filter actions — agent, type, text…"
            aria-label="Filter evidence log"
            className="h-9 w-full rounded-md border border-zinc-800/70 bg-zinc-900/40 pl-8 pr-14 font-mono text-xs text-zinc-300 placeholder:text-zinc-600 focus:border-emerald-500/40 focus:outline-none focus:ring-1 focus:ring-emerald-500/30"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-[10px] tabular-nums text-zinc-600">
            {ordered.length}/{actions.length}
          </span>
        </div>
        {/* type-family chips — same attribution families as the wake cadence card */}
        <div
          className="flex flex-wrap items-center gap-1.5"
          role="group"
          aria-label="Filter evidence by action family"
        >
          {FAMILIES.map((f) => {
            const active = family === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFamily(f.id)}
                aria-pressed={active}
                className={`rounded-md border px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 motion-reduce:transition-none ${
                  active
                    ? EVIDENCE_FAMILY_STYLES[f.id]
                    : "border-zinc-800/70 bg-zinc-900/30 text-zinc-600 hover:border-zinc-700 hover:text-zinc-400"
                }`}
              >
                {f.label}
                <span className="ml-1.5 tabular-nums opacity-70">{familyCounts[f.id]}</span>
              </button>
            );
          })}
        </div>
        {/* outcome chips — honest tri-state over the free-text result field */}
        <div
          className="flex w-full flex-wrap items-center gap-1.5"
          role="group"
          aria-label="Filter evidence by outcome"
        >
          <span className="mr-0.5 font-mono text-[9px] uppercase tracking-wider text-zinc-700">outcome:</span>
          {OUTCOMES.map((o) => {
            const active = outcome === o;
            const label = o === "all" ? "All" : OUTCOME_META[o].label;
            return (
              <button
                key={o}
                type="button"
                onClick={() => setOutcome(o)}
                aria-pressed={active}
                className={`rounded-md border px-2 py-0.5 font-mono text-[9px] font-semibold uppercase tracking-wider transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 motion-reduce:transition-none ${
                  active
                    ? o === "all"
                      ? EVIDENCE_FAMILY_STYLES.all
                      : OUTCOME_META[o].chip
                    : "border-zinc-800/70 bg-zinc-900/30 text-zinc-600 hover:border-zinc-700 hover:text-zinc-400"
                }`}
              >
                {label}
                <span className="ml-1 tabular-nums opacity-70">{outcomeCounts[o]}</span>
              </button>
            );
          })}
        </div>
      </div>
      <ScrollArea className="h-[24rem] rounded-lg border border-zinc-800/70 mission-scroll">
        <ol className="space-y-0 p-1">
          {ordered.map((a) => (
          <li key={a.id} id={`evi-${a.id}`} className="relative -mx-1 rounded-md border-l border-zinc-800/70 px-1 pb-4 pl-5 transition-colors last:pb-1 hover:border-l-emerald-500/50 hover:bg-zinc-900/40">
            <span
              className={`absolute -left-[5px] top-1.5 size-2.5 rounded-full border-2 ${
                outcomeOf(a) === "success"
                  ? "border-emerald-500/60 bg-emerald-500/30"
                  : outcomeOf(a) === "failed"
                    ? "border-rose-500/60 bg-rose-500/30"
                    : outcomeOf(a) === "recorded"
                      ? "border-amber-500/60 bg-amber-500/25"
                      : "border-zinc-600/60 bg-zinc-700/30"
              }`}
              aria-hidden="true"
            />
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="font-mono text-[10px] text-zinc-600">{(a.ts ?? "").slice(0, 16).replace("T", " ")}{a.ts ? "Z" : ""}</span>
              <span
                className={`rounded border px-1.5 py-px font-mono text-[9px] font-semibold uppercase tracking-wider ${
                  TYPE_STYLES[a.type ?? "bootstrap"] ?? TYPE_STYLES.bootstrap
                }`}
              >
                {a.type ?? "action"}
              </span>
              <span className="font-mono text-[10px] text-zinc-500">{a.agent ?? "cron-agent"}</span>
              {outcomeOf(a) === "success" ? (
                <CircleCheck className="size-3.5 text-emerald-500" aria-label="success" />
              ) : outcomeOf(a) === "failed" ? (
                <Ban className="size-3.5 text-rose-500" aria-label="failed" />
              ) : outcomeOf(a) === "recorded" ? (
                <Info
                  className="size-3.5 text-amber-500/80"
                  aria-label="free-text outcome recorded — see entry text"
                />
              ) : (
                <CircleDashed className="size-3.5 text-zinc-600" aria-label="no outcome recorded" />
              )}
            </div>
            <p className="mt-1 text-xs leading-relaxed text-zinc-300">{a.summary ?? a.action ?? "(no summary recorded)"}</p>
            {a.evidence && (
              <p className="mt-1 flex items-center gap-1 font-mono text-[10px] text-zinc-600">
                <Link2 className="size-3" aria-hidden="true" />
                evidence: {a.evidence}
              </p>
            )}
          </li>
        ))}
        {ordered.length === 0 && (
          <li className="flex min-h-[8rem] items-center justify-center font-mono text-xs text-zinc-600">
            no actions match the current filters
          </li>
        )}
      </ol>
    </ScrollArea>
    </div>
  );
}

/* ------------------------------- next actions ------------------------------ */

const PRIORITY_STYLES: Record<string, string> = {
  P0: "border-rose-500/40 bg-rose-500/10 text-rose-400",
  P1: "border-amber-500/40 bg-amber-500/10 text-amber-400",
  P2: "border-zinc-600/40 bg-zinc-700/20 text-zinc-300",
  P3: "border-zinc-700/40 bg-zinc-800/30 text-zinc-500",
};

export function NextActionsView({ queue }: { queue: NextAction[] }) {
  return (
    <ol className="space-y-2.5">
      {queue.map((na, i) => (
        <motion.li
          key={na.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i * 0.04, 0.3), duration: 0.3 }}
        >
          <Card className="border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors hover:border-zinc-700">
            <CardContent className="flex flex-col gap-2 p-4 sm:flex-row sm:items-start sm:gap-4">
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={`inline-flex items-center rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold tracking-wider ${
                    PRIORITY_STYLES[na.priority] ?? PRIORITY_STYLES.P3
                  }`}
                >
                  {na.priority}
                </span>
                {(na.status ?? "").includes("blocked") ? (
                  <Clock className="size-4 text-amber-500/70" aria-label="blocked by earlier step" />
                ) : na.status === "recurring" ? (
                  <ArrowUpRight className="size-4 text-emerald-500/70" aria-label="recurring" />
                ) : (
                  <CircleDashed className="size-4 text-zinc-600" aria-label="pending" />
                )}
              </div>
              <div className="min-w-0 flex-1 space-y-1.5">
                <p className="text-sm font-medium leading-snug text-zinc-200">{na.title}</p>
                <p className="break-words text-xs leading-relaxed text-zinc-500">{na.rationale ?? "— no rationale recorded"}</p>
                <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-600">
                  assigned: {na.assigned ?? "unassigned"}
                  {na.blocked_by ? ` · blocked by ${na.blocked_by}` : ""}
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.li>
      ))}
    </ol>
  );
}

/* ------------------------------ decision log ------------------------------- */

function renderMdLite(text: string) {
  // Minimal, safe renderer: bold + list markers only. Output text nodes,
  // never dangerouslySetInnerHTML — no injection surface.
  const lines = text.split("\n");
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) return null;
        const bullet = trimmed.startsWith("-") || trimmed.startsWith("*");
        const content = bullet ? trimmed.replace(/^[-*]\s+/, "") : trimmed;
        const parts = content.split(/\*\*(.+?)\*\*/g);
        return (
          <p
            key={i}
            className={
              bullet
                ? "flex gap-1.5 text-[11px] leading-relaxed text-zinc-400"
                : "text-[11px] leading-relaxed text-zinc-400"
            }
          >
            {bullet && (
              <span className="mt-1.5 size-1 shrink-0 rounded-full bg-zinc-600" aria-hidden="true" />
            )}
            <span>
              {parts.map((part, j) =>
                j % 2 === 1 ? (
                  <strong key={j} className="font-semibold text-zinc-200">
                    {part}
                  </strong>
                ) : (
                  <span key={j}>{part}</span>
                ),
              )}
            </span>
          </p>
        );
      })}
    </div>
  );
}

export function DecisionsView({ decisions }: { decisions: DecisionEntry[] }) {
  const [open, setOpen] = useState<string | null>(null);
  /* timeline/palette jumps auto-expand the target decision card */
  useMissionFocus("dec", (id) => setOpen(id));
  if (decisions.length === 0) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-8 font-mono text-xs text-zinc-600">
        no decisions recorded yet
      </div>
    );
  }
  return (
    <div className="space-y-2.5">
      {[...decisions].reverse().map((d, i) => {
        const isOpen = open === d.id;
        return (
          <motion.div
            key={d.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.05, 0.25), duration: 0.3 }}
          >
            <Card id={`dec-${d.id}`} className="border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors hover:border-zinc-700">
              <CardContent className="p-4">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-sm text-left focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-500/50"
                  onClick={() => setOpen((o) => (o === d.id ? null : d.id))}
                  aria-expanded={isOpen}
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <Gavel className="size-4 shrink-0 text-violet-400/80" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="flex items-baseline gap-2 text-sm font-medium text-zinc-200">
                        <span className="font-mono text-[10px] text-violet-400/80">{d.id}</span>
                        <span className="truncate">{d.title}</span>
                      </p>
                      <p className="mt-0.5 font-mono text-[10px] text-zinc-600">
                        {d.ts.slice(0, 16).replace("T", " ")}Z
                      </p>
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronDown className="size-4 shrink-0 text-emerald-400" aria-hidden="true" />
                  ) : (
                    <ChevronRight className="size-4 shrink-0 text-zinc-600" aria-hidden="true" />
                  )}
                </button>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25 }}
                    className="mt-3 space-y-3 border-t border-zinc-800/60 pt-3"
                  >
                    <div>
                      <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        Context
                      </p>
                      {renderMdLite(d.context)}
                    </div>
                    <div>
                      <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                        Reasoning
                      </p>
                      {renderMdLite(d.reasoning)}
                    </div>
                    <div className="rounded-md border border-emerald-500/25 bg-emerald-500/5 p-3">
                      <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-emerald-400">
                        Decision
                      </p>
                      {renderMdLite(d.decision)}
                    </div>
                    {d.trigger && (
                      <div>
                        <p className="mb-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-amber-400/80">
                          What would change this
                        </p>
                        {renderMdLite(d.trigger)}
                      </div>
                    )}
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}

/* ------------------------------ research library --------------------------- */

export function ResearchView({ research }: { research: ResearchDoc[] }) {
  const [openDoc, setOpenDoc] = useState<ResearchDoc | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async (doc: ResearchDoc) => {
    setOpenDoc(doc);
    setContent(null);
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(doc.url_path, { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content?: string };
      setContent(json.content ?? "");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  /* the command palette can deep-open a research note by filename */
  useEffect(() => {
    const handler = (e: Event) => {
      const filename = (e as CustomEvent<{ filename?: string }>).detail?.filename;
      const doc = research.find((r) => r.filename === filename);
      if (doc) open(doc);
    };
    window.addEventListener("mission:open-research", handler);
    return () => window.removeEventListener("mission:open-research", handler);
  }, [research]);

  if (research.length === 0) {
    return (
      <div className="flex min-h-[12rem] items-center justify-center rounded-lg border border-dashed border-zinc-800 bg-zinc-900/20 p-8 font-mono text-xs text-zinc-600">
        no research documents recorded yet
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      <p className="text-[11px] leading-relaxed text-zinc-600">
        Evidence-gated research notes with dated sources — every rejected or viable verdict traces
        back to these documents. Read-only.
      </p>
      {research.map((doc, i) => (
        <motion.div
          key={doc.filename}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min(i * 0.05, 0.2), duration: 0.3 }}
        >
          <Card className="border-zinc-800/70 bg-zinc-900/40 shadow-none transition-colors hover:border-zinc-700">
            <CardContent className="flex flex-col gap-2.5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-md border border-violet-500/30 bg-violet-500/10">
                  <FileText className="size-4 text-violet-300" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-zinc-200">{doc.title}</p>
                  <p className="mt-0.5 font-mono text-[10px] text-zinc-600">
                    {doc.date} · {(doc.size_bytes / 1024).toFixed(1)} KB · {doc.headings.length} sections
                  </p>
                  {doc.headings.length > 0 && (
                    <p
                      className="mt-1 line-clamp-1 font-mono text-[10px] text-zinc-500"
                      title={doc.headings.join(" · ")}
                    >
                      {doc.headings.join(" · ")}
                    </p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => open(doc)}
                className="shrink-0 border-zinc-700/60 bg-zinc-800/30 font-mono text-[11px] text-zinc-300 hover:border-emerald-500/40 hover:text-emerald-300 focus-visible:ring-1 focus-visible:ring-emerald-500/50 print:hidden"
              >
                <BookOpen className="size-3.5" aria-hidden="true" />
                Read
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      ))}

      <Dialog open={openDoc !== null} onOpenChange={(o) => !o && setOpenDoc(null)}>
        <DialogContent className="max-h-[85vh] border-zinc-800 bg-zinc-950 sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-mono text-sm text-zinc-100">
              <FileText className="size-4 text-violet-300" aria-hidden="true" />
              {openDoc?.title}
            </DialogTitle>
            <DialogDescription className="font-mono text-[10px] text-zinc-500">
              {openDoc?.filename} · {openDoc ? (openDoc.size_bytes / 1024).toFixed(1) : ""} KB · dated{" "}
              {openDoc?.date} · evidence-gated research note
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] rounded-md border border-zinc-800/70 bg-zinc-900/30 mission-scroll">
            <pre className="whitespace-pre-wrap break-words p-4 font-mono text-[11px] leading-relaxed text-zinc-300">
              {loading ? "loading document…" : error ? `error: ${error}` : (content ?? "")}
            </pre>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}
